
import { MAX_CLIENT_ID_LENGTH, MAX_MESSAGE_BYTES, MAX_UTTERANCE_CHARS } from './constants';
import type { ClientMessage } from './types';

export type ValidationResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

const fail = (error: string): { ok: false; error: string } => ({ ok: false, error });
const pass = <T>(value: T): { ok: true; value: T } => ({ ok: true, value });


const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function str(v: unknown, max: number): string | null {
  if (typeof v !== 'string') return null;
  if (v.length > max) return null;
  return v;
}

function num(v: unknown, min: number, max: number): number | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  if (v < min || v > max) return null;
  return v;
}

function int(v: unknown, min: number, max: number): number | null {
  const n = num(v, min, max);
  if (n === null || !Number.isInteger(n)) return null;
  return n;
}

function nullableStr(v: unknown, max: number): string | null | undefined {
  if (v === null) return null;
  const s = str(v, max);
  return s === null ? undefined : s;
}

function nullableInt(v: unknown, min: number, max: number): number | null | undefined {
  if (v === null) return null;
  const n = int(v, min, max);
  return n === null ? undefined : n;
}


const encoder = new TextEncoder();

export function frameTooLarge(data: string | ArrayBuffer): boolean {
  if (typeof data !== 'string') return data.byteLength > MAX_MESSAGE_BYTES;
  if (data.length * 3 <= MAX_MESSAGE_BYTES) return false;
  return encoder.encode(data).byteLength > MAX_MESSAGE_BYTES;
}

export function parseClientMessage(
  data: string | ArrayBuffer,
): ValidationResult<ClientMessage> {
  if (typeof data !== 'string') return fail('expected a text frame');
  if (frameTooLarge(data)) return fail('message too large');

  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return fail('malformed JSON');
  }
  if (!isObject(parsed)) return fail('expected a JSON object');

  const type = parsed['type'];
  if (typeof type !== 'string') return fail('missing type');

  switch (type) {
    case 'join': {
      const clientId = str(parsed['clientId'], MAX_CLIENT_ID_LENGTH);
      if (clientId === null || clientId.length === 0) return fail('join: bad clientId');
      return pass({ type: 'join', clientId });
    }

    case 'offer':
    case 'answer': {
      const sdp = str(parsed['sdp'], MAX_MESSAGE_BYTES);
      if (sdp === null || sdp.length === 0) return fail(`${type}: bad sdp`);
      return pass({ type, sdp });
    }

    case 'ice-candidate': {
      const candidate = str(parsed['candidate'], 2048);
      if (candidate === null) return fail('ice-candidate: bad candidate');
      const sdpMid = nullableStr(parsed['sdpMid'], 64);
      if (sdpMid === undefined) return fail('ice-candidate: bad sdpMid');
      const sdpMLineIndex = nullableInt(parsed['sdpMLineIndex'], 0, 255);
      if (sdpMLineIndex === undefined) return fail('ice-candidate: bad sdpMLineIndex');
      const usernameFragment = nullableStr(parsed['usernameFragment'], 256);
      if (usernameFragment === undefined) {
        return fail('ice-candidate: bad usernameFragment');
      }
      return pass({
        type: 'ice-candidate',
        candidate,
        sdpMid,
        sdpMLineIndex,
        usernameFragment,
      });
    }

    case 'speech': {
      const text = str(parsed['text'], MAX_UTTERANCE_CHARS);
      if (text === null || text.trim().length === 0) return fail('speech: bad text');
      const seq = int(parsed['seq'], 0, Number.MAX_SAFE_INTEGER);
      if (seq === null) return fail('speech: bad seq');
      return pass({ type: 'speech', text, seq });
    }

    case 'rematch':
      return pass({ type: 'rematch' });

    case 'claim-forfeit':
      return pass({ type: 'claim-forfeit' });

    case 'false-positive': {
      const word = str(parsed['word'], 64);
      if (word === null || word.length === 0) return fail('false-positive: bad word');
      const atMs = num(parsed['atMs'], 0, Number.MAX_SAFE_INTEGER);
      if (atMs === null) return fail('false-positive: bad atMs');
      return pass({ type: 'false-positive', word, atMs });
    }

    default:
      return fail(`unknown message type: ${type.slice(0, 32)}`);
  }
}


export interface TokenBucket {
  tokens: number;
  lastRefillMs: number;
}

export function newBucket(burst: number, nowMs: number): TokenBucket {
  return { tokens: burst, lastRefillMs: nowMs };
}

export function takeToken(
  bucket: TokenBucket,
  nowMs: number,
  burst: number,
  perSecond: number,
): boolean {
  const elapsedMs = Math.max(0, nowMs - bucket.lastRefillMs);
  bucket.tokens = Math.min(burst, bucket.tokens + (elapsedMs / 1000) * perSecond);
  bucket.lastRefillMs = nowMs;
  if (bucket.tokens < 1) return false;
  bucket.tokens -= 1;
  return true;
}
